import { KCOM_DOMAIN_REGEX } from "@/constants/regex"

/**
 * Gets panel titles from a navigation object
 * @param {Object} obj - Navigation object
 * @return {Array<string>} Array of panel titles
 */
export function getBrandTitles(obj) {
  if (!obj) return null
  // Check if object has required properties
  if (!obj[":itemsOrder"] || !obj[":items"]) return obj["items"]
  // Get an array of titles
  const titles = obj[":itemsOrder"].map(key => {
    const item = obj[":items"][key]
    return item["brand_switcher"] || ""
  })
  return titles
}

const normalizeHostname = (value, base) => {
  if (!value) return ""

  try {
    return new URL(value, base).hostname.replace(/^www\./, "")
  } catch {
    return ""
  }
}

const isInternalLink = (link, origin) => {
  if (!link) return false
  if (link.startsWith("/")) return true

  const originHostname = normalizeHostname(origin)
  const linkHostname = normalizeHostname(link, origin)

  if (!linkHostname) return false

  return (
    KCOM_DOMAIN_REGEX.test(linkHostname) ||
    Boolean(
      originHostname &&
        (linkHostname === originHostname ||
          linkHostname.endsWith(`.${originHostname}`) ||
          originHostname.endsWith(`.${linkHostname}`))
    )
  )
}

/**
 * Generates an array of brand objects from the given input object.
 *
 * @param {Object} obj - The input object containing brand information.
 * @param {Object} i18n - The internationalization object for localized strings.
 * @param {string} origin - Stable site origin used for analytics URL matching.
 * @param {string} eventPageType - Stable page type used for analytics naming.
 * @return {Array} An array of brand objects with text, href, target, and isActive properties.
 */
export function getBrandsArray(
  obj,
  i18n,
  origin = process.env.NEXT_PUBLIC_WEBSITE_URL || "",
  eventPageType = ""
) {
  const brandSwitcherArray = getBrandTitles(obj) || []
  const brandArray = brandSwitcherArray
    .map(item => {
      if (!item) return { text: "", href: "", target: "_self", isActive: false }
      return {
        text: item?.brandName || "",
        href: item?.link || "",
        target: item?.linkToNewTab === "true" ? "_blank" : "_self",
        isActive: item?.highlight || false,
        "aria-label": `${item?.brandName} ${
          item?.linkToNewTab === "true"
            ? i18n.opensInNewTab
            : i18n.opensInSameTab
        }`,
        trackingAttributes: {
          "data-datalayer-id": "kds-element-navigation",
          "data-datalayer-data": JSON.stringify({
            clickInternalLinks: isInternalLink(item?.link, origin),
            eventAction: "brand sync",
            eventName: "brand sync",
            eventType: "click",
            internalLinkName: `brand sync:${item?.brandName?.toLowerCase()}`,
            internalLinkPosition: "global header",
            internalLinkType: "global header:navigation",
            internalLinkURL: item?.link,
            internalLinkZoneName: eventPageType
              ? `${eventPageType}:global header`
              : "global header",
          }),
        },
      }
    })
    .filter(item => item.text || item.href) // Filter out empty items
  return brandArray
}
